import { useState } from "react";

export const CPF_LENGTH = {
  WITH_MASK: 14,
  WITHOUT_MASK: 11,
} as const;

type CpfChangeEvent = { target: { value: string } };

export const extractCpfDigits = (value: string): string =>
  value.replace(/\D/g, "").slice(0, CPF_LENGTH.WITHOUT_MASK);

export const applyCpfMaskIfNeeded = (value: string): string => {
  const numbersOnly: string = extractCpfDigits(value);

  if (numbersOnly.length >= 4) {
    return numbersOnly
      .slice(0, CPF_LENGTH.WITHOUT_MASK)
      .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4")
      .replace(/\.$/, "")
      .replace(/-$/, "");
  }

  return value;
};

export const maskCpf = (cpf: string) => applyCpfMaskIfNeeded(cpf);

export const redactCpf = (cpf: string): string => {
  const digits: string = cpf.replace(/\D/g, "");

  if (digits.length !== CPF_LENGTH.WITHOUT_MASK) return cpf;

  return `***.${digits.slice(3, 6)}.${digits.slice(6, 9)}-**`;
};

const useCpfMask = () => {
  const [cpfMask, setCpfMask] = useState("");
  const [cpf, setCpf] = useState("");
  const [isValid, setIsValid] = useState(false);

  const validateCpf = (cpf: string) => {
    const cpfClean = cpf.replace(/\D/g, "");
    if (cpfClean.length !== CPF_LENGTH.WITHOUT_MASK) return false;

    let sum = 0;
    let remainder;

    for (let i = 1; i <= 9; i++) {
      sum += parseInt(cpfClean.substring(i - 1, i)) * (11 - i);
    }

    remainder = (sum * 10) % 11;

    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpfClean.substring(9, 10))) return false;

    sum = 0;
    for (let i = 1; i <= 10; i++) {
      sum += parseInt(cpfClean.substring(i - 1, i)) * (12 - i);
    }

    remainder = (sum * 10) % 11;

    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cpfClean.substring(10, 11))) return false;

    return true;
  };

  const handleCpfChange = (e: CpfChangeEvent) => {
    let value = e.target.value.replace(/\D/g, "");

    if (value.length > 3) {
      value = value.slice(0, 3) + "." + value.slice(3);
    }
    if (value.length > 7) {
      value = value.slice(0, 7) + "." + value.slice(7);
    }
    if (value.length > 11) {
      value = value.slice(0, 11) + "-" + value.slice(11);
    }
    if (value.length > CPF_LENGTH.WITH_MASK) {
      value = value.slice(0, CPF_LENGTH.WITH_MASK);
    }

    setCpfMask(value);

    if (value.replace(/\D/g, "").length === CPF_LENGTH.WITHOUT_MASK) {
      setIsValid(validateCpf(value));
      setCpf(value);
    } else {
      setIsValid(false);
    }
  };

  const cleanCpf = (cpf: string) => {
    return cpf.replace(/\D/g, "");
  };

  return {
    cpfMask,
    cpf,
    isValid,
    handleCpfChange,
    cleanCpf,
  };
};

export default useCpfMask;
